// Cloud sync hook — fetches the user's full Supabase students list,
// derives the active cloud student from the local activeChild's
// supabaseStudentId link, and exposes per-active-child sync helpers.
//
// Pre-extraction this lived in App.jsx alongside auth + helpers. Pulling
// it out keeps App.jsx focused on routing + state composition. Behavior
// unchanged — same shape / same semantics.
//
// All cloud calls are wrapped in withTimeout (8s cap) so a flaky network
// doesn't hang the app on the Loading screen forever. The onCloudOffline
// callback fires once when the cloud first becomes unreachable; App
// uses it to surface a toast and a persistent offline pill.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchStudents, createStudent, updateStudent, updateStudentLevelsSafe, saveSession as saveSessionToCloud, upsertDailyLog, fetchConsentRecords } from "../supabaseClient.js";
import { captureSupabaseError } from "../lib/sentry";
import { createEpochLatch } from "./cloudSyncGuard.js";
import { runGatedCloudCreate } from "./consentGate.js";
import { getUserRole } from "../lib/userRole";
import { deriveIsTeacherAccount, resolveEffectiveRole, fetchOwnsClass } from "../lib/teacherAccount.ts";
import { isDevBypass } from "../lib/persistence";
import { shouldShowZeroActiveRecovery } from "./onboardingHoldGuard.js";
import { syncStudentRow } from "./mascotPullMerge.js";

// MA-629 §3a — exported so supabaseClient.js's FETCH_STUDENTS_SELF_DEADLINE_MS
// can be pinned strictly below this at test time (the cross-module relation
// there is no shared constant today; a future edit to either number must not
// silently re-open the timeout hazard that relation guards against).
export const CLOUD_TIMEOUT_MS = 8000;

function withTimeout(promise, ms = CLOUD_TIMEOUT_MS, label = "cloud") {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timeout after ${ms}ms`)), ms)),
  ]);
}

export default function useCloudSync(user, activeSupabaseStudentId, onCloudOffline) {
  const [students, setStudents] = useState([]);
  // MA-581 — WHOSE list `students` currently holds. `students` alone cannot
  // answer that: `user?.id` can change before `students` does, so a consumer
  // comparing local links against `students` during an account switch would be
  // measuring account B against account A's data. Set ONLY in the fetch success
  // branch, cleared on sign-out, and NEVER touched in the catch — so a
  // transient failure leaves it stale-but-honest rather than falsely current.
  const [studentsOwnerId, setStudentsOwnerId] = useState(null);
  // MA-629 §3b — tri-state positive-proof-of-emptiness signal from
  // fetchStudents' owned-student probe (true / false / null — never a bare
  // count). `null` initially: unknown, keep today's behaviour until a fetch
  // resolves. Painted ONLY inside the epoch latch's isCurrent(token) guard,
  // alongside setStudents/setStudentsOwnerId — a superseded account's answer
  // must never paint (MA-581's rule, applied to this signal too).
  const [hasAnyOwnedStudent, setHasAnyOwnedStudent] = useState(null);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudOffline, setCloudOffline] = useState(false);
  // cloudError — a sentinel SPECIFIC to the initial students-fetch outcome
  // (null on success, the Error on failure). Distinct from cloudOffline on
  // purpose: cloudOffline is the broad "any cloud call failed" signal that
  // owns the toast + offline pill and is ALSO set by write-failures
  // (createCloudStudent / syncToCloud). The auth-gate ladder must NOT gate
  // re-onboarding on write-failures — a returning parent with a transient
  // save error still has their kids; blocking them would be worse than the
  // bug we're fixing. So the ladder reads THIS narrow fetch-only sentinel
  // (see App.jsx shouldHoldForCloudError + P-AUTH-GATE-AND-ONBOARDING-DRAFT).
  // It tracks ONLY the students-fetch (refreshStudents), never the create
  // path, and clears to null the moment a fetch succeeds.
  const [cloudError, setCloudError] = useState(null);

  // Last-started-wins guard against the cross-user data flash: a fast account
  // switch (sign-out → sign-in) re-fires the [user?.id] effect for user B
  // while user A's fetchStudents() is still in flight. Without this, A's
  // resolve would setStudents(A's kids) under B's session. One latch per hook
  // instance (in a ref so all overlapping fetches share the sequence).
  const latchRef = useRef(createEpochLatch());

  // Pulled-out fetch so onboarding can call it after createCloudStudent
  // succeeds, refreshing the students list without waiting for the next
  // user-id change.
  const refreshStudents = useCallback(async () => {
    if (!user) { setStudents([]); setStudentsOwnerId(null); setHasAnyOwnedStudent(null); setCloudError(null); return []; }
    const token = latchRef.current.begin();
    try {
      // MA-581 — fetchStudents now reports the owner it ACTUALLY queried
      // (null when there was no session), so the stamp can never be a guess.
      // MA-629 — and now also the tri-state hasAnyOwnedStudent, riding the
      // SAME resolved tuple as rows so there is no frame where students has
      // painted and the probe answer has not.
      const { rows: s, ownerId: fetchedOwnerId, hasAnyOwnedStudent: fetchedHasAnyOwnedStudent } = await withTimeout(
        fetchStudents(),
        CLOUD_TIMEOUT_MS,
        "fetchStudents",
      );
      // A newer refresh superseded us (account switch). Return the data we
      // fetched to our own awaiter, but DO NOT paint it into shared state —
      // that would flash a stale account's students under the new session.
      if (!latchRef.current.isCurrent(token)) return s;
      setStudents(s);
      // MA-581 — stamp the owner the QUERY used, not the React user.id, and
      // null when there was no session (which fails the trust check open).
      setStudentsOwnerId(fetchedOwnerId);
      // MA-629 — painted only here, inside the same isCurrent(token) guard as
      // setStudents/setStudentsOwnerId (MA-581's rule for studentsOwnerId,
      // applied identically to this signal).
      setHasAnyOwnedStudent(fetchedHasAnyOwnedStudent);
      setCloudOffline(false);
      // Fetch succeeded → clear the gate sentinel. A previously-failed boot
      // that then recovers (e.g. the parent taps Retry on the CloudErrorRetry
      // surface) clears here, so the auth-gate ladder stops holding and the
      // hydrated students flow through to the normal path.
      setCloudError(null);
      return s;
    } catch (err) {
      // Stale failure: suppress the offline toast/callback too — it belongs to
      // a superseded account, not the session now on screen.
      if (!latchRef.current.isCurrent(token)) return [];
      captureSupabaseError("fetchStudents failed", err, { userId: user?.id });
      setCloudOffline(true);
      // Record the fetch-specific failure for the auth-gate ladder. Carrying
      // the Error itself (not a bare boolean) lets the gate breadcrumb + the
      // retry surface report what went wrong.
      setCloudError(err instanceof Error ? err : new Error(String(err)));
      // MA-629 §3b — deliberately UNLIKE studentsOwnerId, which the catch
      // block never touches. Stated honestly rather than overclaimed: `null`
      // = unknown = fail closed is still the right value to leave this at,
      // but on THIS path it is DEFENCE IN DEPTH, not currently load-bearing —
      // `cloudError` is set in this same batch, and
      // `shouldShowZeroActiveRecovery` checks `if (cloudError) return false`
      // before it ever reads `hasAnyOwnedStudent`, so today's hold decision
      // on a failed fetch is already settled by that earlier short-circuit
      // regardless of what this line does. This reset exists for the shape
      // of state a future caller could reach if `hasAnyOwnedStudent` were
      // ever consulted independently of `cloudError` (e.g. a UI surfacing
      // "some students may be hidden" without gating on cloudError first) —
      // in that shape, a stale non-null value would be read as settled proof
      // it is not. Do not "fix" this into matching studentsOwnerId's
      // asymmetry: the two signals still have opposite failure-safe
      // directions on purpose (an untrustworthy owner stamp must stay
      // stale-but-honest for MA-581's intersection check; an untrustworthy
      // owned-probe answer must never be mistaken for a settled one).
      setHasAnyOwnedStudent(null);
      if (typeof onCloudOffline === "function") onCloudOffline(err);
      return [];
    }
  }, [user?.id, onCloudOffline]);

  useEffect(() => {
    // MA-581 — clear the owner stamp on sign-out too. Without this, a
    // sign-out -> sign-in of the SAME account leaves `studentsOwnerId` set
    // while `students` is [], so for one paintable frame the owned set reads
    // as trustworthy-and-empty and every kid is filtered out of the pickers.
    if (!user) { setStudents([]); setStudentsOwnerId(null); setHasAnyOwnedStudent(null); setCloudOffline(false); setCloudError(null); return; }
    // alive guards setCloudLoading against a late resolve from a superseded
    // account (epoch latch already guards setStudents/setCloudOffline). On
    // teardown — including the account-switch re-fire — alive flips false so a
    // stale finally() can't clear the spinner the new fetch just raised.
    let alive = true;
    setCloudLoading(true);
    refreshStudents().finally(() => {
      if (alive) setCloudLoading(false);
    });
    return () => { alive = false; };
  }, [user?.id]);

  // MA-395 (engine-truth-2026-09-08.md §E2, secondary gap) — `students` is
  // this plain useState, filled only by the [user?.id] effect above (boot)
  // or the two CloudErrorRetry buttons. No interval, no visibilitychange, no
  // route-change refetch, and the global query client has
  // `refetchOnWindowFocus: false` (main.jsx) — so a laptop left open never
  // sees a skill assigned on another device until it is reloaded. Re-pull on
  // tab-visible / window-focus, debounced so a school tablet that backgrounds
  // constantly doesn't hammer fetchStudents; the epoch latch above already
  // handles any overlap with an in-flight boot fetch.
  const lastVisibleRefreshRef = useRef(0);
  const VISIBLE_REFRESH_DEBOUNCE_MS = 5000;
  useEffect(() => {
    if (!user) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastVisibleRefreshRef.current < VISIBLE_REFRESH_DEBOUNCE_MS) return;
      lastVisibleRefreshRef.current = now;
      void refreshStudents();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [user?.id, refreshStudents]);

  // Derive the active cloud student from the activeChild link. Memoized
  // so syncToCloud / saveCloudSession callbacks see a stable identity
  // per (activeSupabaseStudentId, students) tuple.
  const cloudStudent = useMemo(
    () => (activeSupabaseStudentId ? students.find((s) => s.id === activeSupabaseStudentId) || null : null),
    [students, activeSupabaseStudentId],
  );

  // MA-1024 — the students leg is the pure syncStudentRow, whose payload is
  // buildStudentSyncPayload(progress): mascot_id is sent ONLY while
  // progress.mascotPendingSync is set (an unconfirmed local pick), so a
  // routine call can never write a stale pal (or null) over another
  // device's newer pick. The resolved value names the pal the returned row
  // confirmed (null after a PGRST204 strip), and the caller clears the flag.
  const syncToCloud = useCallback(async (p) => {
    if (!cloudStudent) return null;
    // mascot_id is gated by a column-exists check — see updateStudent in
    // supabaseClient. If the migration hasn't been applied, the column
    // is silently dropped from the payload so the rest of the sync still
    // succeeds.
    // math/reading levels use a guarded writer (MA-39) to prevent a stale
    // device from pulling those fields backward after a fresher device
    // already wrote. Levels have no natural domain date to guard on, so
    // p.levelsUpdatedAt — stamped locally at the moment the level actually
    // changed, not at sync time — is the guard token; progress predating the
    // MA-39 levels stamp has no such field, so it falls back to "now"
    // (identical to the pre-guard blind-write behaviour, never a regression
    // for un-stamped progress).
    try {
      const [studentLeg] = await Promise.all([
        syncStudentRow({ studentId: cloudStudent.id, progress: p, updateStudentFn: updateStudent }),
        updateStudentLevelsSafe(cloudStudent.id, {
          mathLevelId: p.mathModuleId,
          readingLevelId: p.readingModuleId,
          levelsUpdatedAt: p.levelsUpdatedAt || new Date().toISOString(),
        }),
      ]);
      return { studentId: cloudStudent.id, mascotConfirmed: studentLeg.mascotConfirmed };
    } catch (e) { console.error(e); return null; }
  }, [cloudStudent]);

  const saveCloudSession = useCallback(async (domain, correct, total, feeling) => {
    if (!cloudStudent) return;
    try {
      const lid = domain === "math" ? cloudStudent.math_level_id : cloudStudent.reading_level_id;
      await saveSessionToCloud({ studentId: cloudStudent.id, subject: domain, levelId: lid, correct, total, feeling, results: [] });
      await upsertDailyLog(cloudStudent.id, domain);
    } catch (e) { console.error(e); }
  }, [cloudStudent]);

  const createCloudStudentFn = useCallback(async (parentId, data) => {
    // MA-04 — COPPA consent gate (single chokepoint). This is the sole caller
    // of createStudent, so ALL child-PII cloud writes (onboarding, settings
    // add-kid, lazy-create) funnel through this gate — nothing can bypass it.
    // No active un-revoked consent → return null (local-only), the SAME
    // soft-fail contract as the offline catch below, so onboarding still
    // completes locally and nothing hard-breaks. Dev bypass (?dev=1) skips the
    // gate so e2e/dev (no real session) are unaffected.
    const devBypass = isDevBypass();
    // MA-557 (MA-DEC-0824 D1/D2/D4) — the role that decides the consent lane
    // is the EFFECTIVE role: device intent (getUserRole()) combined with the
    // server-side account fact (owns a class OR holds the completed
    // school-authority-direct row — deriveIsTeacherAccount, teacherAccount.ts).
    // STALENESS-FREE BY CONSTRUCTION, not by luck: the fact is fetched FRESH
    // right here rather than read from useIsTeacherAccount()'s cached query.
    // onboarding.kid.handoff.tsx AWAITS recordSchoolAuthorityDirectConsent
    // before calling finish(), which is what eventually reaches this
    // function (finish -> handleOnboardingComplete -> createCloudStudent) —
    // so a first-time teacher's qualifying consent row can land moments
    // before this runs, and a 60s-staleTime cached hook value could still
    // read the pre-write state and wrongly deny the row just written.
    // Reusing THIS SAME freshly-fetched rows snapshot as the gate's own
    // `fetchConsent` below (instead of letting guardCloudChildWrite issue a
    // second, independent fetch) closes the window completely: the rows
    // that decide "is this a teacher account" and the rows the gate checks
    // satisfaction against are the exact same read, not two reads that could
    // race or land on different sides of the write. Skipped under dev
    // bypass — guardCloudChildWrite short-circuits before role is ever
    // consulted there, and dev/e2e has no real session to query anyway.
    const [ownsClass, freshConsentRows] = devBypass
      ? [false, []]
      : await Promise.all([
          fetchOwnsClass(parentId).catch(() => false),
          fetchConsentRecords(parentId).catch(() => []),
        ]);
    const isTeacherAccount = deriveIsTeacherAccount({ ownsClass, consentRows: freshConsentRows });
    return runGatedCloudCreate({
      parentId,
      devBypass,
      role: resolveEffectiveRole(getUserRole(), isTeacherAccount),
      fetchConsent: async () => freshConsentRows,
      onBlocked: (gate) =>
        captureSupabaseError(
          "createCloudStudent blocked: no active COPPA consent",
          new Error(gate.reason),
          { parentId },
        ),
      insert: async () => {
        try {
          const s = await withTimeout(createStudent(parentId, data), CLOUD_TIMEOUT_MS, "createStudent");
          // Prepend so the caller sees it immediately even before the next refresh.
          setStudents((prev) => [s, ...prev.filter((p) => p.id !== s.id)]);
          setCloudOffline(false);
          return s;
        } catch (e) {
          captureSupabaseError("createCloudStudent failed", e, { parentId });
          setCloudOffline(true);
          if (typeof onCloudOffline === "function") onCloudOffline(e);
          return null;
        }
      },
    });
  }, [onCloudOffline]);

  // MA-629 §3b — the derived hold verdict, computed inline on EVERY render,
  // deliberately NOT useMemo'd: the predicate reads the clock (its `now`
  // default), and `user`'s identity is stable across the five-minute
  // RETURNING_PARENT_MIN_ACCOUNT_AGE_MS crossing, so a
  // `useMemo(() => shouldShowZeroActiveRecovery({...}), [cloudError,
  // cloudLoading, students, user, hasAnyOwnedStudent])` would capture `now` at
  // memo time and never recompute as the account ages past the threshold.
  // Evaluating it unconditionally is inert for a null `user` — the guard is
  // pure and returns `false` via isReturningProvisionedParent's own
  // `if (!user || !user.created_at) return false` — and per-render `now` is
  // exactly what App.jsx's own call site did before this hook owned it.
  const zeroActiveRecovery = shouldShowZeroActiveRecovery({
    cloudError,
    cloudLoading,
    students,
    user,
    hasAnyOwnedStudent,
  });

  return { students, studentsOwnerId, cloudStudent, cloudLoading, cloudOffline, cloudError, zeroActiveRecovery, syncToCloud, saveCloudSession, createCloudStudent: createCloudStudentFn, refreshStudents };
}
